import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { type LocationRow, useLocations } from '../../../features/locations/use-locations'

const mono = { fontFamily: 'var(--v2-font-mono)' } as const
const SKELETON = [52, 38, 61, 44, 70, 35, 56, 48, 66, 40, 58, 46, 63, 36]

function StatusDot({ status }: { status: LocationRow['status'] }) {
  const active = status === 'Active'
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full"
        style={{ background: active ? 'var(--v2-ok)' : 'var(--v2-off)' }}
      />
      <span className={active ? 'text-[var(--color-fg)]' : 'text-[var(--color-muted)]'}>{status}</span>
    </span>
  )
}

function Note({ children, error }: { children: ReactNode; error?: boolean }) {
  return (
    <div
      role={error ? 'alert' : 'status'}
      className="px-4 py-3 text-[12px] text-[var(--color-muted)]"
      style={error ? { color: 'var(--color-fg)', boxShadow: 'inset 2px 0 0 var(--color-accent)' } : undefined}
    >
      {children}
    </div>
  )
}

export function SignalLocations() {
  const { data, isLoading, error } = useLocations()
  const [q, setQ] = useState('')
  const [cursor, setCursor] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!data) return []
    if (!needle) return data
    return data.filter((r) => r.code.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle))
  }, [data, q])

  const last = Math.max(rows.length - 1, 0)
  const cur = Math.min(cursor, last)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === '/') {
        e.preventDefault()
        input.current?.focus()
      } else if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        setCursor((c) => Math.min(c + 1, last))
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        setCursor((c) => Math.max(c - 1, 0))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [last])

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-row="${cur}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [cur])

  const total = data?.length ?? 0
  const activeCount = data?.filter((r) => r.status === 'Active').length ?? 0

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-11 shrink-0 items-center gap-3 border-b border-[var(--color-border)] px-4">
        <h1 className="text-[14px] font-semibold tracking-[-0.01em]">Locations</h1>
        {data && (
          <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
            {rows.length === total ? total : `${rows.length}/${total}`} · {activeCount} active
          </span>
        )}
        <div className="relative ml-auto">
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setCursor(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQ('')
                e.currentTarget.blur()
              } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault()
                e.currentTarget.blur()
              }
            }}
            placeholder="Filter by code or name"
            aria-label="Filter locations"
            spellCheck={false}
            className="h-7 w-[240px] rounded-[4px] border border-[var(--color-border)] bg-[var(--color-surface)] pr-7 pl-2 text-[12px] text-[var(--color-fg)] placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
          />
          <kbd
            className="pointer-events-none absolute top-1/2 right-1.5 -translate-y-1/2 rounded-[3px] border border-[var(--color-border)] px-1 text-[10px] leading-[14px] text-[var(--color-muted)]"
            style={mono}
          >
            /
          </kbd>
        </div>
      </div>

      <div ref={list} className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <Note error>Failed to load locations: {error.message}</Note>
        ) : (
          <table className="w-full table-fixed border-collapse text-left" aria-label="Locations" aria-busy={isLoading}>
            <colgroup>
              <col style={{ width: 104 }} />
              <col />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[var(--color-surface)]">
              <tr className="h-7 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]">
                <th scope="col" className="border-b border-[var(--color-border)] pl-4 font-medium">
                  Code
                </th>
                <th scope="col" className="border-b border-[var(--color-border)] font-medium">
                  Name
                </th>
                <th scope="col" className="border-b border-[var(--color-border)] pr-4 font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                SKELETON.map((w) => (
                  <tr key={w} className="h-7">
                    <td className="border-b border-[var(--color-border)] pl-4">
                      <span className="block h-2 w-10 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
                    </td>
                    <td className="border-b border-[var(--color-border)]">
                      <span
                        className="block h-2 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]"
                        style={{ width: `${w}%` }}
                      />
                    </td>
                    <td className="border-b border-[var(--color-border)] pr-4">
                      <span className="block h-2 w-14 animate-pulse rounded-[2px] bg-[var(--color-surface-2)]" />
                    </td>
                  </tr>
                ))}
              {rows.map((r, i) => {
                const on = i === cur
                return (
                  <tr
                    key={`${r.code}-${i}`}
                    data-row={i}
                    aria-selected={on}
                    onClick={() => setCursor(i)}
                    className="h-7 cursor-default text-[12px] hover:bg-[var(--color-surface-2)]"
                    style={{
                      background: on ? 'var(--v2-accent-soft)' : undefined,
                      boxShadow: on ? 'inset 2px 0 0 var(--color-accent)' : undefined,
                    }}
                  >
                    <td className="truncate border-b border-[var(--color-border)] pl-4" style={mono}>
                      {r.code || '-'}
                    </td>
                    <td className="truncate border-b border-[var(--color-border)]">{r.name || '-'}</td>
                    <td className="border-b border-[var(--color-border)] pr-4">
                      <StatusDot status={r.status} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <Note>{q ? `No locations match "${q}". Esc to clear.` : 'No locations.'}</Note>
        )}
      </div>

      <div
        className="flex h-6 shrink-0 items-center gap-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-[11px] text-[var(--color-muted)]"
        style={mono}
      >
        <span>j/k move</span>
        <span>/ filter</span>
        <span>esc clear</span>
        {rows.length > 0 && (
          <span className="ml-auto">
            {cur + 1} / {rows.length}
          </span>
        )}
      </div>
    </div>
  )
}
